// useOrganization Hook - Organization Context Management
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Organization context management hook for multi-tenant applications
 *
 * @returns {Object} Organization state and management methods
 */
export const useOrganization = () => {
  const { user, isAuthenticated } = useAuth();
  const dispatch = useDispatch();

  // Future RBAC organization state (when implemented)
  // const {
  //   currentOrganization,
  //   availableOrganizations,
  //   loading,
  //   error
  // } = useSelector((state) => state.rbac || {});

  // Current organization info from user object (basic implementation)
  const currentOrganization = useMemo(() => {
    if (!user) return null;

    // Extract organization info from user object
    return {
      id: user.organization_id || user.organisation_id || null,
      name: user.organization_name || user.organisation_name || null,
      code: user.organization_code || user.organisation_code || null,
      type: user.organization_type || user.organisation_type || null,
      settings: user.organization_settings || user.organisation_settings || {},
    };
  }, [user]);

  // Available organizations (basic implementation)
  const availableOrganizations = useMemo(() => {
    if (!user || !user.available_organizations) return [];
    return Array.isArray(user.available_organizations) ? user.available_organizations : [];
  }, [user]);

  // Check if user belongs to an organization
  const hasOrganization = useMemo(() => {
    return !!currentOrganization?.id;
  }, [currentOrganization]);

  // Check if user belongs to a specific organization
  const belongsToOrganization = useCallback(
    (organizationId) => {
      if (!isAuthenticated || !hasOrganization) return false;
      return currentOrganization.id === organizationId;
    },
    [isAuthenticated, hasOrganization, currentOrganization]
  );

  // Check if user can switch organizations
  const canSwitchOrganizations = useMemo(() => {
    return availableOrganizations.length > 1;
  }, [availableOrganizations]);

  // Get organization setting
  const getOrganizationSetting = useCallback(
    (settingKey, defaultValue = null) => {
      if (!currentOrganization?.settings) return defaultValue;
      return currentOrganization.settings[settingKey] ?? defaultValue;
    },
    [currentOrganization]
  );

  // Check if organization has specific feature enabled
  const hasOrganizationFeature = useCallback(
    (feature) => {
      const features = getOrganizationSetting('enabled_features', []);
      return Array.isArray(features) && features.includes(feature);
    },
    [getOrganizationSetting]
  );

  // Get organization limits
  const getOrganizationLimits = useCallback(() => {
    return getOrganizationSetting('limits', {});
  }, [getOrganizationSetting]);

  // Check if organization limit is exceeded
  const isLimitExceeded = useCallback(
    (limitType, currentValue) => {
      const limits = getOrganizationLimits();
      const limit = limits[limitType];

      if (typeof limit !== 'number') return false;
      return currentValue >= limit;
    },
    [getOrganizationLimits]
  );

  // Get organization branding
  const getOrganizationBranding = useCallback(() => {
    return getOrganizationSetting('branding', {
      logo: null,
      primaryColor: null,
      secondaryColor: null,
      companyName: currentOrganization?.name || null,
    });
  }, [getOrganizationSetting, currentOrganization]);

  // Switch organization (placeholder for future implementation)
  const switchOrganization = useCallback(
    async (organizationId) => {
      if (!canSwitchOrganizations) {
        return {
          success: false,
          error: 'Organization switching not available',
        };
      }

      try {
        // Future implementation will dispatch an async action
        // const result = await dispatch(switchOrganization(organizationId)).unwrap();
        // return { success: true, data: result };

        console.warn('Organization switching not yet implemented');
        return { success: false, error: 'Not yet implemented' };
      } catch (error) {
        return { success: false, error };
      }
    },
    [canSwitchOrganizations]
  );

  // Organization type checks
  const isOrganizationType = useCallback(
    (type) => {
      return currentOrganization?.type === type;
    },
    [currentOrganization]
  );

  const isEnterprise = useCallback(() => {
    return isOrganizationType('enterprise');
  }, [isOrganizationType]);

  const isStandard = useCallback(() => {
    return isOrganizationType('standard');
  }, [isOrganizationType]);

  const isTrial = useCallback(() => {
    return isOrganizationType('trial');
  }, [isOrganizationType]);

  // Organization hierarchy checks (for multi-level organizations)
  const isParentOrganization = useCallback(() => {
    return getOrganizationSetting('is_parent', false);
  }, [getOrganizationSetting]);

  const hasChildOrganizations = useCallback(() => {
    const children = getOrganizationSetting('child_organizations', []);
    return Array.isArray(children) && children.length > 0;
  }, [getOrganizationSetting]);

  const getChildOrganizations = useCallback(() => {
    return getOrganizationSetting('child_organizations', []);
  }, [getOrganizationSetting]);

  const getParentOrganization = useCallback(() => {
    return getOrganizationSetting('parent_organization', null);
  }, [getOrganizationSetting]);

  // Organization status checks
  const isActive = useCallback(() => {
    return getOrganizationSetting('status', 'active') === 'active';
  }, [getOrganizationSetting]);

  const isSuspended = useCallback(() => {
    return getOrganizationSetting('status', 'active') === 'suspended';
  }, [getOrganizationSetting]);

  const isExpired = useCallback(() => {
    const expiryDate = getOrganizationSetting('expiry_date', null);
    if (!expiryDate) return false;

    return new Date(expiryDate) < new Date();
  }, [getOrganizationSetting]);

  return {
    // State
    currentOrganization,
    availableOrganizations,
    hasOrganization,
    canSwitchOrganizations,

    // Organization checks
    belongsToOrganization,
    isOrganizationType,
    isEnterprise,
    isStandard,
    isTrial,

    // Status checks
    isActive,
    isSuspended,
    isExpired,

    // Hierarchy checks
    isParentOrganization,
    hasChildOrganizations,
    getChildOrganizations,
    getParentOrganization,

    // Settings and features
    getOrganizationSetting,
    hasOrganizationFeature,
    getOrganizationLimits,
    isLimitExceeded,
    getOrganizationBranding,

    // Actions
    switchOrganization,
  };
};

export default useOrganization;
